import React, { useEffect, useState, useMemo} from 'react'
import axios from 'axios'
import { BASE_URL, STUDY_ABBREVIATION_MAP } from '../../utils/constants';
import { FaBell, FaBoxArchive, FaPaperPlane, FaTrashCan } from "react-icons/fa6";
import { MdExitToApp } from "react-icons/md";
import { Table, Button } from 'react-bootstrap'
import { Oval } from "react-loader-spinner"
import Modal from 'react-bootstrap/Modal'
import { useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import _ from "underscore"
import { AiFillCaretUp, AiFillCaretDown } from "react-icons/ai";
import Pagination from '../../components/Pagination/Pagination';

import "./Notifications.scss"

// Define the custom sort order for milestones
const MILESTONE_ORDER = {
  'pre-treatment': 0,
  '2 weeks': 1,
  '6 weeks': 2,
  '3 months': 3,
  '6 months': 4,
  '1 year': 5,
  '2 years': 6,
  '3 years': 7,
  '4 years': 8,
  '5 years': 9,
  '6 years': 10,
  '7 years': 11,
  '8 years': 12,
  '9 years': 13,
  '10 years': 14,
  '11 years': 15,
  '12 years': 16,
  '13 years': 17,
  '14 years': 18,
  '15 years': 19,
};


const Notifications = ({ user, logUserOut, setNumberOfNotifications }) => {
  const [loading, setLoading] = useState(true)
  const [originalSurveyOutliers, setOriginalSurveyOutliers] = useState([]);
  const [originalTermsAndConditionsRejectedSurveys, setOriginalTermsAndConditionsRejectedSurveys] = useState([]);
  const [surveyOutliers, setSurveyOutliers] = useState([])
  const [termsAndConditionsRejectedSurveys, setTermsAndConditionsRejectedSurveys] = useState([])

  const [outlierToArchive, setOutlierToArchive] = useState({})
  const [termsAndConditionsRejectedSurvey, setTermsAndConditionsRejectedSurvey] = useState({})
  const [showArchiveModal, setShowArchiveModal] = useState("");
  const [showResendSurveyModal, setShowResendSurveyModal] = useState("");
  const [showDeletePatientModal, setShowDeletePatientModal] = useState("");
  const [sorting, setSorting] = useState([{ field: 'alertDate', ascending: false }])
  const [showArchived, setShowArchived] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  }

  useEffect(() => {
    const getArchivedNotifications = async () => {
      try {
        setLoading(true)
        let response = await axios.get(`${BASE_URL}/api/v1/self-assessment/outliers?include-archived=${showArchived}`, config)
        const outliers = response.data.surveyOutliers
        setOriginalSurveyOutliers(outliers)
        setSurveyOutliers(outliers)

        response = await axios.get(`${BASE_URL}/api/v1/self-assessment/terms-and-conditions-rejected/`, config)
        const surveys = response.data.termsAndConditionsRejectedSurveys
        setOriginalTermsAndConditionsRejectedSurveys(surveys)
        setTermsAndConditionsRejectedSurveys(surveys)
        
        setNumberOfNotifications(outliers.filter(outlier => outlier.archived === false).length + surveys.length)
        setLoading(false)
        setCurrentPage(1);
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    getArchivedNotifications()
  }, [showArchived])

  // Multi-sort function
  const multiSort = (a, b) => {
    for (let sortRule of sorting) {
      const { field, ascending } = sortRule;
      let aValue = a[field];
      let bValue = b[field];
      
      // Handle date fields
      if (field === 'alertDate' || field === 'eventDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Handle stage field for terms & conditions rejected surveys
      if (field === 'stage') {
        // For terms & conditions rejected surveys (no vasScore), use event date for sorting
        if (!a.hasOwnProperty('vasScore')) {
          aValue = new Date(a.eventDate);
        }
        if (!b.hasOwnProperty('vasScore')) {
          bValue = new Date(b.eventDate);
        }
      }
      
      // Handle VAS scores (convert to number)
      if (field === 'vasScore') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      // Handle string fields
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return ascending ? -1 : 1;
      if (aValue > bValue) return ascending ? 1 : -1;
    }
    return 0;
  };

  // Sort the combined notifications using multi-sort
  const sortedNotifications = useMemo(() => {
    const allNotifications = [...surveyOutliers, ...termsAndConditionsRejectedSurveys];
    return [...allNotifications].sort(multiSort);
  }, [sorting, surveyOutliers, termsAndConditionsRejectedSurveys])

  const handleSort = (field, event) => {
    const isShiftPressed = event.shiftKey;
    const isCtrlPressed = event.ctrlKey || event.metaKey;

    if (isCtrlPressed) {
      setSorting([{ field: 'alertDate', ascending: false }]);
      return;
    }

    setSorting(prevSorting => {
      const existingSortIndex = prevSorting.findIndex(s => s.field === field);
      
      if (existingSortIndex > -1) {
        return prevSorting.map((sortItem, index) => {
          if (index === existingSortIndex) {
            return { ...sortItem, ascending: !sortItem.ascending };
          }
          return sortItem;
        });
      } else {
        if (isShiftPressed) {
          return [...prevSorting, { field, ascending: true }];
        } else {
          return [{ field, ascending: true }];
        }
      }
    });
    setCurrentPage(1); // Reset to page 1 when sorting changes
  };

  const renderSortIndicator = (field) => {
    const sortIndex = sorting.findIndex(s => s.field === field);
    if (sortIndex === -1) return "";
    
    const { ascending } = sorting[sortIndex];
    return (
      <>
        {ascending ? <AiFillCaretUp /> : <AiFillCaretDown />}
        {sorting.length > 1 && <span className="sort-priority">{sortIndex + 1}</span>}
      </>
    );
  };

  const resetSorting = () => {
    setSorting([{ field: 'alertDate', ascending: false }]);
  };
  
  const handleArchive = async () => {
    try {
      const body = {
        vasId: outlierToArchive.vasId
      }
      setShowArchiveModal(false)
      await axios.post(`${BASE_URL}/api/v1/self-assessment/outliers/handle`, body, config)
      toast.success('Notification archived!', {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: "light",
      });
      setSurveyOutliers(previousSurveyOutliers => previousSurveyOutliers.filter(surveyOutlier => surveyOutlier.vasId !== outlierToArchive.vasId))
      setNumberOfNotifications(previousNumberOfNotifications => previousNumberOfNotifications - 1)
    }
    catch (error) {
      console.log(error)
      const message = error.response && error.response.data.message ? error.response.data.message : error.message
      if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
        logUserOut()
      }
    }
  }

  const handleResendSurvey = async () => {
    try {
      const body = {
        customPatientId: termsAndConditionsRejectedSurvey.customPatientId,
        eventId: termsAndConditionsRejectedSurvey.eventId,
        study: termsAndConditionsRejectedSurvey.study,
        treatmentSide: termsAndConditionsRejectedSurvey.treatmentSide
      }
      setShowResendSurveyModal(false)
      await axios.post(`${BASE_URL}/api/v1/event/${termsAndConditionsRejectedSurvey.eventId}/survey/resend-pre-treatment-survey`, body, config)
      toast.success('Resend pre-treatment survey!', {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: "light",
      });
      setTermsAndConditionsRejectedSurveys(previousTermsAndConditionsRejectedSurveys => previousTermsAndConditionsRejectedSurveys.filter(survey => survey.customPatientId !== termsAndConditionsRejectedSurvey.customPatientId))
      setNumberOfNotifications(previousNumberOfNotifications => previousNumberOfNotifications - 1)
    }
    catch (error) {
      console.log(error)
      const message = error.response && error.response.data.message ? error.response.data.message : error.message
      if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
        logUserOut()
      }
    }
  }

  const handleDeletePatient = async () => {
    try {
      setShowDeletePatientModal(false)
      await axios.delete(`${BASE_URL}/api/v1/patient/${termsAndConditionsRejectedSurvey.customPatientId}`, config)
      toast.success(`Patient ${termsAndConditionsRejectedSurvey.customPatientId} has been deleted`, {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: "light",
      });
      setTermsAndConditionsRejectedSurveys(previousTermsAndConditionsRejectedSurveys => previousTermsAndConditionsRejectedSurveys.filter(survey => survey.customPatientId !== termsAndConditionsRejectedSurvey.customPatientId))
      setNumberOfNotifications(previousNumberOfNotifications => previousNumberOfNotifications - 1)
    }
    catch (error) {
      console.log(error)
      const message = error.response && error.response.data.message ? error.response.data.message : error.message
      if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
        logUserOut()
      }
    }
  }

  // Pagination Logic for UI-side pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedNotifications.slice(indexOfFirstItem, indexOfLastItem);
  const totalItems = sortedNotifications.length;

  return (
    <div className="notifications">
      {loading ? (<Oval
          height={80}
          width={80}
          color="#4caeff"
          wrapperStyle={{}}
          wrapperClass="loader"
          visible={true}
          ariaLabel='oval-loading'
          secondaryColor="#4caeff"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
        ) : (
        <>
          <Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Archive notification</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to archive this notification?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowArchiveModal(false)}>
                Close
              </Button>
              <Button variant="primary" onClick={handleArchive}>
                Archive
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showResendSurveyModal} onHide={() => setShowResendSurveyModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Resend survey</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to resend this survey?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowResendSurveyModal(false)}>
                Close
              </Button>
              <Button variant="primary" onClick={handleResendSurvey}>
                Resend
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal show={showDeletePatientModal} onHide={() => setShowDeletePatientModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Delete patient</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to delete this patient?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeletePatientModal(false)}>
                Close
              </Button>
              <Button variant="danger" onClick={handleDeletePatient}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
          
          <div className="notifications-header">
            <div className="header-left">
              <h2 className="logo"><FaBell /> Notifications</h2>
            </div>
            <div className="header-controls">
              {sorting.length > 1 && (
                <Button variant="outline-primary" size="sm" onClick={resetSorting} className="reset-sort-btn">
                  Reset Sort
                </Button>
              )}
            </div>
          </div>

          <div className='notifications-table'>
            <Table striped bordered={false} hover>
              <thead>
                <tr>
                  <th className='alert-date' onClick={(e) => handleSort("alertDate", e)}>Alert Date {renderSortIndicator("alertDate")}</th>
                  <th className='custom-id' onClick={(e) => handleSort("customPatientId", e)}>Custom Patient ID {renderSortIndicator("customPatientId")}</th>
                  <th className='study' onClick={(e) => handleSort("study", e)}>Study {renderSortIndicator("study")}</th>
                  <th className='alert-details' onClick={(e) => handleSort("vasScore", e)}>Alert Details {renderSortIndicator("vasScore")}</th>
                  <th className='milestone' onClick={(e) => handleSort("stage", e)}>Milestone / Date {renderSortIndicator("stage")}</th>
                  <th className='actions'>Actions</th>
                  <th className='show-hide-archived'>
                    <div className="archive-toggle-container">
                      <span className="toggle-label">Archived</span>
                      <div className={`archive-toggle ${showArchived ? 'active' : ''}`} onClick={() => setShowArchived(!showArchived)}>
                        <div className="toggle-slider"></div>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? currentItems.map((item, index) => {
                  if (item.hasOwnProperty('vasScore')) {
                    const surveyOutlier = item;
                    return (
                      <tr key={index}>
                        <td className="alert-date">{moment(surveyOutlier.alertDate).format("DD/MM/YYYY")}</td>
                        <td className="custom-id">{surveyOutlier.customPatientId}</td>
                        <td className="study">{STUDY_ABBREVIATION_MAP[surveyOutlier.study]} ({surveyOutlier.treatmentSide[0]})</td>
                        <td className="alert-details">VAS = {surveyOutlier.vasScore}</td>
                        <td className="milestone">{surveyOutlier.stage}</td>
                        <td className="actions"><Link to={`/patient/${surveyOutlier.customPatientId}/event/${surveyOutlier.eventId}/${surveyOutlier.study.replace(" ", "-")}/${surveyOutlier.treatmentSide.toLowerCase()}/survey/${surveyOutlier.stage.replace(" ", "-")}/edit`}>View Survey</Link></td>
                        <td className='notification-action-buttons'>
                          <div>
                            <Button variant="warning" onClick={() => {setOutlierToArchive(surveyOutlier); setShowArchiveModal(true)}} disabled={surveyOutlier.archived}><FaBoxArchive /></Button>
                            <Button variant="primary" onClick={() => navigate(`/patients/${surveyOutlier.customPatientId}`)}><MdExitToApp /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  } else {
                    const survey = item;
                    return (
                      <tr key={index}>
                        <td className="alert-date">n/a</td>
                        <td className="custom-id">{survey.customPatientId}</td>
                        <td className="study">{STUDY_ABBREVIATION_MAP[survey.study]} ({survey.treatmentSide[0]})</td>
                        <td className="alert-details">T&C rejected</td>
                        <td className="milestone">{moment(survey.eventDate).format("DD/MM/YYYY")}</td>
                        <td className="actions"><Link to={`/patient/${survey.customPatientId}/event/${survey.eventId}/${survey.study.replace(" ", "-")}/${survey.treatmentSide.toLowerCase()}/survey/pre-treatment/edit`}>View Survey</Link></td>
                        <td className='terms-and-condition-action-buttons'>
                          <div>
                            <Button variant="success" onClick={() => {setTermsAndConditionsRejectedSurvey(survey); setShowResendSurveyModal(true)}}><FaPaperPlane /></Button>
                            <Button variant="danger" onClick={() => {setTermsAndConditionsRejectedSurvey(survey); setShowDeletePatientModal(true)}}><FaTrashCan /></Button>
                            <Button variant="primary" onClick={() => navigate(`/patients/${survey.customPatientId}`)}><MdExitToApp /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                }) : <tr><td colSpan={100} style={{textAlign: "center"}}>No active notifications</td></tr>}
              </tbody>
            </Table>
          </div>
          
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </>)}    
    </div>
  )
}

export default Notifications;